import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { TableInitialState } from '@work4all/components';
import { TablePrefilterProvider } from '@work4all/components/lib/components/table/TablePrefilterProvider';
import { TableVariantProvider } from '@work4all/components/lib/components/table/TableVariantProvider';
import { useCanGoBack } from '@work4all/components/lib/hooks';
import { useLockSubscription } from '@work4all/components/lib/hooks/object-lock-subscription/LockObjectProvider';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { CustomFieldsConfigProvider } from '@work4all/data/lib/custom-fields';
import { useDocumentClasses } from '@work4all/data/lib/hooks/document-classes';

import { getDefaultPrefilter } from '@work4all/models';
import { Filters } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { convertEntityToBzObjType } from '@work4all/models/lib/utils/convertEntityToBzObjType';

import { CalculationsTable } from '../..//components/data-tables/CalculationsTable';
import {
  AddressLinksTable,
  BranchOfficesTable,
  ProjectKindTable,
  ProjectStatusTable,
  RelationsTable,
  ResourceClassTable,
  TicketCategory1Table,
  TicketCategory2Table,
  TicketChecklistTable,
  TicketKindTable,
  TopicTable,
} from '../../components/data-tables';
import { AppointmentStateTable } from '../../components/data-tables/appointment-state-table/AppointmentStateTable';
import { AppointmentsTable } from '../../components/data-tables/appointments-table/AppointmentsTable';
import { ArticleTable } from '../../components/data-tables/article-table/ArticleTable';
import { CallMemoTable } from '../../components/data-tables/CallMemoTable';
import { ChecklistsTable } from '../../components/data-tables/ChecklistsTable';
import { ContractsTable } from '../../components/data-tables/ContractsTable';
import { CostCenterTable } from '../../components/data-tables/cost-center-table/CostCenterTable';
import { CountryTable } from '../../components/data-tables/country-table/CountryTable';
import { CurrencyTable } from '../../components/data-tables/currency-table/CurrencyTable';
import { CustomersTable } from '../../components/data-tables/CustomersTable';
import { DeliveryNotesTable } from '../../components/data-tables/DeliveryNotesTable';
import { DemandsTable } from '../../components/data-tables/DemandsTable';
import { DocumentsTable } from '../../components/data-tables/DocumentsTable';
import { EMailSignatureTable } from '../../components/data-tables/EmailSignatureTable';
import { EmailsTable } from '../../components/data-tables/EmailsTable';
import { EmailTemplateTable } from '../../components/data-tables/EmailTemplateTable';
import { IEntityTableV8 } from '../../components/data-tables/entity-table/EntityTableV8';
import { FileServiceProviderConfigTable } from '../../components/data-tables/file-service-provider-config-table/FileServiceProviderConfigTable';
import { HolidayTable } from '../../components/data-tables/holiday-table/HolidayTable';
import { InboundDeliveryNotesTable } from '../../components/data-tables/InboundDeliveryNotesTable';
import { InvoicesTable } from '../../components/data-tables/InvoicesTable';
import { LanguageTable } from '../../components/data-tables/language-table/LanguageTable';
import { LedgerAccountTable } from '../../components/data-tables/ledger-account-table/LedgerAccountTable';
import { LettersTable } from '../../components/data-tables/LettersTable';
import { MailboxContentTable } from '../../components/data-tables/MailboxContentTable/MailboxContentTable';
import { NotesTable } from '../../components/data-tables/NotesTable';
import { NumberingLogicTable } from '../../components/data-tables/numbering-logic-table/NumberingLogicTable';
import { ErpPositionsTable } from '../../components/data-tables/offers-table/erp-common/ErpPositionsTable';
import { OffersTable } from '../../components/data-tables/offers-table/OffersTable';
import { OrdersTable } from '../../components/data-tables/OrdersTable';
import { ProductionContractsTable } from '../../components/data-tables/ProductionContractsTable';
import { ContributionMarginTable } from '../../components/data-tables/project-controlling-columns/ContributionMarginTable';
import { ProjectControllingTable } from '../../components/data-tables/project-controlling-columns/ProjectControllingTable';
import { ProjectHoursTable } from '../../components/data-tables/project-hours-table/ProjectHoursTable';
import { ProjectRightsTableGrid } from '../../components/data-tables/project-table/ProjectRightsTableGrid';
import { ProjectTable } from '../../components/data-tables/project-table/ProjectTable';
import { RaBookingsTable } from '../../components/data-tables/ra-bookings-table/RaBookingsTable';
import { RaPaymentsTable } from '../../components/data-tables/ra-payments-table/RaPaymentsTable';
import { RaTable } from '../../components/data-tables/ra-table/RaTable';
import { ReApprovalTable } from '../../components/data-tables/re-approval-table/ReApprovalTable';
import { ReBookingsTable } from '../../components/data-tables/re-bookings-table/ReBookingsTable';
import { RePaymentsTable } from '../../components/data-tables/re-payments-table/RePaymentsTable';
import { ReTable } from '../../components/data-tables/re-table/ReTable';
import { SalesOpportunitiesTable } from '../../components/data-tables/SalesOpportunitiesTable';
import { SalutationTable } from '../../components/data-tables/salutation-table/SalutationTable';
import { ServiceContractPositionTable } from '../../components/data-tables/ServiceContractPositionTable';
import { ServiceContractTable } from '../../components/data-tables/ServiceContractTable';
import { SuppliersTable } from '../../components/data-tables/SuppliersTable';
import { TasksTable } from '../../components/data-tables/tasks-table/TasksTable';
import { TenantBankAccountTable } from '../../components/data-tables/tenant-bank-account-table/TenantBankAccountTable';
import { TenantTable } from '../../components/data-tables/tenant-table/TenantTable';
import { TextBuildingBlockTable } from '../../components/data-tables/text-building-block-table/TextBuildingBlockTable';
import { TicketTimeTrackingTable } from '../../components/data-tables/ticket-time-tracking-table/TicketTimeTrackingTable';
import { TicketsTable } from '../../components/data-tables/TicketsTable';
import { TimeTrackingTable } from '../../components/data-tables/time-tracking-table/TimeTrackingTable';
import { TravelExpensesTable } from '../../components/data-tables/TravelExpensesTable';
import { TravelReceiptsTable } from '../../components/data-tables/TravelReceiptsTable';
import { UnitTable } from '../../components/data-tables/unit-table/UnitTable';
import { UsersTable } from '../../components/data-tables/users-table/UsersTable';
import { VacationApprovalTable } from '../../components/data-tables/vacation-approval-table/VacationApprovalTable';
import { VacationKindTable } from '../../components/data-tables/vacation-kind-table/VacationKindTable';
import { VisitationReportsTable } from '../../components/data-tables/VisitationReportsTable';
import { WebShopTable } from '../../components/data-tables/web-shop-table/WebShopTable';
import { WordDocumentTemplateTable } from '../../components/data-tables/WordDocumentTemplateTable';
import { WordLetterTemplateTable } from '../../components/data-tables/WordLetterTemplateTable';
import { ListPage } from '../../components/list-page/ListPage';
import { usePageTitle } from '../../hooks/use-page-title';
import { RE_APPROVAL_ENTITY } from '../approval/containers/invoices/InvoicesApprovalContainer';
import { VACATION_APPROVAL_ENTITY } from '../approval/containers/vacations/VacationsApprovalContainer';

import { ContentWrapper } from './components/contentWrapper/ContentWrapper';
import { ExtendedEntities } from './ExtendedEntities.enum';
import { ListPageContext, ListPageContextValue } from './list-page-context';
import { useApendCustomListSettings } from './useApendCustomListSettings';

export const EntityToTable = {
  [Entities.visitationReport]: VisitationReportsTable,
  [Entities.checkList]: ChecklistsTable,
  [Entities.calculation]: CalculationsTable,
  [Entities.offer]: OffersTable,
  [Entities.ticket]: TicketsTable,
  [Entities.deliveryNote]: DeliveryNotesTable,
  [Entities.inboundDeliveryNote]: InboundDeliveryNotesTable,
  [Entities.invoice]: InvoicesTable,
  [Entities.document]: DocumentsTable,
  [Entities.letter]: LettersTable,
  [Entities.order]: OrdersTable,
  [Entities.reViewModel]: ReTable,
  [Entities.demand]: DemandsTable,
  [Entities.task]: TasksTable,
  [Entities.tenant]: TenantTable,
  [Entities.tenantBankAccount]: TenantBankAccountTable,
  [Entities.fileServiceProviderConfig]: FileServiceProviderConfigTable,
  [Entities.note]: NotesTable,
  [Entities.callMemo]: CallMemoTable,
  [Entities.eMail]: EmailsTable,
  [Entities.contract]: ContractsTable,
  [Entities.travelExpenses]: TravelExpensesTable,
  [Entities.travelReceipts]: TravelReceiptsTable,
  [Entities.appointment]: AppointmentsTable,
  [Entities.project]: ProjectTable,
  [Entities.customer]: CustomersTable,
  [Entities.supplier]: SuppliersTable,
  [Entities.productionContract]: ProductionContractsTable,
  [Entities.article]: ArticleTable,
  [Entities.wordLetterTemplate]: WordLetterTemplateTable,
  [Entities.wordDocumentTemplate]: WordDocumentTemplateTable,
  [Entities.eMailTemplate]: EmailTemplateTable,
  [Entities.eMailSignature]: EMailSignatureTable,
  [Entities.textBuildingBlock]: TextBuildingBlockTable,
  [Entities.salesOpportunities]: SalesOpportunitiesTable,
  [Entities.mailboxContent]: MailboxContentTable,
  [Entities.raViewModel]: RaTable,
  'project(rights)': ProjectRightsTableGrid,
  [Entities.position]: ErpPositionsTable,
  'positionDetails(offer)': ErpPositionsTable,
  'positionDetails(calculation)': ErpPositionsTable,
  'positionDetails(demand)': ErpPositionsTable,
  'positionDetails(contract)': ErpPositionsTable,
  'positionDetails(deliveryNote)': ErpPositionsTable,
  'positionDetails(order)': ErpPositionsTable,
  'positionDetails(inboundDeliveryNote)': ErpPositionsTable,
  'positionDetails(invoice)': ErpPositionsTable,
  'lookUp(projectKind)': ProjectKindTable,
  'lookUp(projectStatus)': ProjectStatusTable,
  'lookUp(addressLinks)': AddressLinksTable,
  'lookUp(relations)': RelationsTable,
  'lookUp(ticketChecklist)': TicketChecklistTable,
  'lookUp(ticketCategory2)': TicketCategory2Table,
  'lookUp(branchOffices)': BranchOfficesTable,
  'lookUp(resourceClass)': ResourceClassTable,

  [Entities.ticketCategory1]: TicketCategory1Table,
  [Entities.ticketKind]: TicketKindTable,
  [Entities.topic]: TopicTable,

  [Entities.raPayment]: RaPaymentsTable,
  [Entities.raLedgerAccountSplit]: RaBookingsTable,
  [Entities.rELedgerAccountSplit]: ReBookingsTable,
  [Entities.inboundInvoicePayment]: RePaymentsTable,
  [Entities.fullUser]: UsersTable,
  [Entities.timeTracking]: TimeTrackingTable,
  [Entities.serviceContract]: ServiceContractTable,
  [Entities.serviceContractPosition]: ServiceContractPositionTable,
  [Entities.appointmentState]: AppointmentStateTable,
  [Entities.holiday]: HolidayTable,
  [Entities.costCenter]: CostCenterTable,
  [Entities.baseDataLanguage]: LanguageTable,
  [Entities.unit]: UnitTable,
  [Entities.numberingLogic]: NumberingLogicTable,
  [Entities.ledgerAccount]: LedgerAccountTable,
  [Entities.salutation]: SalutationTable,
  [Entities.currency]: CurrencyTable,
  reApproval: ReApprovalTable,
  vacationApproval: VacationApprovalTable,
  [Entities.webShopDefinition]: WebShopTable,
  projectHours: ProjectHoursTable,
  targetActualComparison: ProjectControllingTable,
  contributionMargin: ContributionMarginTable,
  [Entities.vacationKind]: VacationKindTable,
  [Entities.ticketTimeTracking]: TicketTimeTrackingTable,
  [Entities.country]: CountryTable,
};

interface TableViewProps {
  entityType: string;
  prefilter?: Filters;
  entityVariant?: string;
  initialState?: TableInitialState;
  tableComponentProps?: IEntityTableV8;
}
const CONST_ARRAY = [];
const LIST_LIKE = [
  'project(rights)',
  'positionDetails(offer)',
  'positionDetails(calculation)',
  'positionDetails(demand)',
  'positionDetails(contract)',
  'positionDetails(deliveryNote)',
  'positionDetails(order)',
  'positionDetails(inboundDeliveryNote)',
  'positionDetails(invoice)',
  'lookUp(projectKind)',
  'lookUp(projectStatus)',
  'lookUp(addressLinks)',
  'lookUp(relations)',
  'lookUp(ticketChecklist)',
  'lookUp(ticketCategory2)',
  'lookUp(branchOffices)',
  'lookUp(resourceClass)',
];

export const EntityTableView = ({
  entityType,
  prefilter = CONST_ARRAY,
  entityVariant = '',
  initialState,
  tableComponentProps,
}: TableViewProps) => {
  const mappedEntityType = useMemo<Entities | null>(() => {
    if (['targetActualComparison', 'contributionMargin'].includes(entityType)) {
      return Entities.project;
    }
    if (entityType === RE_APPROVAL_ENTITY) {
      return Entities.reViewModel;
    } else if (entityType === VACATION_APPROVAL_ENTITY) {
      return Entities.vacationRequestEntity;
    } else if (!(entityType in Entities)) {
      return null;
    }

    return entityType as Entities;
  }, [entityType]);

  useLockSubscription(mappedEntityType);

  const listPageContextValue = useMemo<ListPageContextValue | null>(
    () => (mappedEntityType ? { entityType: mappedEntityType } : null),
    [mappedEntityType]
  );

  if (!entityType) {
    return <div>TODO</div>;
  }

  const nameOfVariant = `${entityType}(${entityVariant})`;
  const TableComp = LIST_LIKE.includes(nameOfVariant)
    ? EntityToTable[nameOfVariant]
    : EntityToTable[entityType];
  // Add `key` to <CustomFieldsConfigProvider> to unmount the component tree
  // when navigating from one list page to another. This is required because of
  // the current table state management implementation.

  return (
    <TableVariantProvider value={{ variant: entityVariant }}>
      <TablePrefilterProvider value={{ prefilter }}>
        <ListPageContext.Provider value={listPageContextValue}>
          <CustomFieldsConfigProvider
            key={entityType}
            entity={mappedEntityType}
          >
            <ListPage
              renderTable={(tableApiRef) => {
                return (
                  <TableComp
                    ref={tableApiRef}
                    prefilter={prefilter}
                    {...tableComponentProps}
                  />
                );
              }}
              initialState={initialState}
            />
          </CustomFieldsConfigProvider>
        </ListPageContext.Provider>
      </TablePrefilterProvider>
    </TableVariantProvider>
  );
};

export function resolveEntityVariant(entityTypeParam: string): {
  entityType: ExtendedEntities;
  entityVariant: string | null;
  prefilter: Filters | null;
} {
  const regex = /^(?<entityName>.+?)\((?<entityVariant>.*)\)$/;

  const result = regex.exec(entityTypeParam);

  // This does not look like a virtual entity. Just treat it as normal entity
  // and return full string.
  if (result === null) {
    if (entityTypeParam === Entities.document) {
      return {
        entityType: Entities.document,
        entityVariant: '',
        prefilter: getDefaultPrefilter(Entities.document),
      };
    }

    return {
      entityType: entityTypeParam as Entities,
      entityVariant: null,
      prefilter: getDefaultPrefilter(entityTypeParam as Entities),
    };
  }

  const { entityName, entityVariant } = result.groups;

  return {
    entityType: entityName as Entities,
    entityVariant,
    prefilter: LIST_LIKE.includes(entityTypeParam)
      ? getPrefilterByEntityVariant(entityName, entityVariant)
      : getDefaultPrefilter(Entities.document, entityVariant),
  };
}

export const FileEntitiesListsController: React.FC<{
  amplitudeEntryPoint?: string;
  entityType?: string;
}> = (props) => {
  const { entityType: entityTypeParam, fileType } = useParams<{
    entityType: Entities;
    fileType: Entities;
  }>();

  const fileEntityType = entityTypeParam || props.entityType;

  const { amplitudeEntryPoint = fileType } = props;

  const resolved = resolveEntityVariant(fileEntityType);
  const { entityType, entityVariant, prefilter } = resolved;
  const documentClasses = useDocumentClasses();

  const isEntityTypeRecognized = useMemo(() => {
    if (['targetActualComparison', 'contributionMargin'].includes(entityType)) {
      return true;
    }

    if (!(entityType in Entities)) {
      return false;
    }

    if (entityType !== Entities.document) {
      return true;
    }

    return (
      entityVariant === '' ||
      documentClasses.some((documentClass) => {
        return documentClass.name === entityVariant;
      })
    );
  }, [entityType, entityVariant, documentClasses]);

  useApendCustomListSettings(entityType);

  useEffect(() => {
    sendAmplitudeData(EventType.OpenList, {
      name: entityType,
      entryPoint: amplitudeEntryPoint,
    });
  }, [amplitudeEntryPoint, entityType]);

  const [searchParams] = useSearchParams();

  const { t, i18n } = useTranslation();

  let pageTitle =
    t(`LIST.${fileEntityType}.TITLE`, { defaultValue: '' }) ||
    entityVariant ||
    t(`LIST.${entityType.toUpperCase()}.TITLE`, { defaultValue: '' }) ||
    t(`COMMON.${entityType.toUpperCase()}`, { count: 2 });
  const customizedTitle = searchParams.get('customizedTitle');

  if (entityType === Entities.lookUp) {
    pageTitle = t(`COMMON.${entityVariant.toUpperCase()}`, { count: 2 });
  }

  if (customizedTitle) {
    pageTitle = i18n.exists(customizedTitle)
      ? t(customizedTitle)
      : customizedTitle;
  }

  usePageTitle(pageTitle);

  const location = useLocation();
  const canGoBack = useCanGoBack();

  const closeUrl = location.pathname.replace(/\/entity.*/, '');

  const TableComp = EntityToTable[fileEntityType] || EntityToTable[entityType];

  return (
    <ContentWrapper
      backAction={canGoBack ? 'back' : 'close'}
      closeUrl={closeUrl}
      entity={entityType}
      amplitudeEntryPoint={amplitudeEntryPoint}
      title={isEntityTypeRecognized && TableComp ? pageTitle : '404 Not Found'}
      data-test-id="list-page-wrapper"
    >
      {isEntityTypeRecognized && TableComp && (
        <EntityTableView
          entityType={entityType}
          prefilter={prefilter}
          entityVariant={entityVariant}
        />
      )}
    </ContentWrapper>
  );
};

function getPrefilterByEntityVariant(entityName, entityVariant) {
  if (entityName === Entities.positionDetails) {
    return [
      {
        pos_RealBzObjType: {
          $eq: convertEntityToBzObjType(entityVariant),
        },
      },
    ];
  }
  return null;
}
